import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useForm } from 'antd/es/form/Form';

import QuotationService from '../../../../../../../shared/services/quotation';

import { GenericDataFormContext } from '../GenericDataForm';
import { StepOneFormContext } from './StepOneFormContext';

import {
  AdditionalDataClient,
  Client,
  QuotationClient,
} from '../../../../../../../shared/interfaces';

import { ClientDataTableGrid, LOADING_SAVED_QUOTATIONS_STATES, LoadSavedQuotations, QuotationStep } from '../../interfaces';

import { StepOneForm, StepOneFormRepresentative } from '../../../utils';
import { QuotationInfoContext } from '../QuotationInfo';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../interfaces/loading-renewal-quotation-states-enum';
import { LoadQuotationToRenewResponse } from '../../interfaces/load-renewal-quotation-response-interface';
import { LegalRepresentative } from '../../../../../../../shared/interfaces/services/insert-legal-representative-service';
import { fixEncoding } from '../../../../../../../shared/services/insurancePolicy/utils';

export const StepOneFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const normalizeOptionalValue = (value: unknown): string => {
    if (value === undefined || value === null) return '';
    return String(value);
  };
  const { 
    ivaConditionList, 
    documentTypeList, 
    typePersonList, 
    provinceList, 
    getGenericDataForm,
    getLocalitiesPerProvince,
  } = useContext(GenericDataFormContext);

  const { 
    currentStep, 
    savedQuotation, 
    loadingSavedQuotation, 
    loadSaveQuotationState, 
    setLoadSaveQuotationState,
    quotationInfo,
    quotationToRenewal,
    renewalQuotationState,
    setRenewalQuotationState
  } = useContext(QuotationInfoContext);

  const [clientListModalIsOpen, setClientListModalIsOpen] =
    useState<boolean>(false);
  const [isRepresentativeModal, setIsRepresentativeModal] =
    useState<boolean>(false);
  const [clients, setClients] = useState<Array<Client>>([]);
  const [clientSelected, setClientSelected] = useState<QuotationClient>();
  const [additionalDataClientSelected, setAdditionalDataClientSelected] = useState<AdditionalDataClient>();
  const [legalRepresentativeSelected, setLegalRepresentativeSelected] = useState<LegalRepresentative[] | undefined>(undefined);
  const [isLoadingClientList, setIsLoadingClientList] =
    useState<boolean>(false);
  const [stepOneForm, setStepOneForm] = useState<StepOneForm>();
  const [tableClientList, setTableClientList] = useState<
    Array<ClientDataTableGrid>
  >([]);

  const [clientDataForm] = useForm<StepOneForm>();
  const [legalRepresentativeDataForm] = useForm<StepOneFormRepresentative>();

  const onSelectClientOnTable = useCallback(
    async ( codigo: string ) => {      
      const client = clients.find((client) => client.codigo === codigo);
      
      const ivaCondition = ivaConditionList?.find(
        (el) => el.label?.toLowerCase() === client?.descripcionIva?.toLowerCase()
      )?.value;

      const documentTypeValue = documentTypeList?.find(
          (el) => el.label?.toLowerCase() === client?.tipoDeDocumento?.toLowerCase()
        )?.value;

      const typePersonValue = typePersonList?.find(
        (el) => el.value.toLowerCase() === client?.tipoPersona.toLowerCase()
      )?.value;

      const provinceValue = provinceList?.find(
        (el) => el.value.toLowerCase() === client?.codProvincia.toLowerCase()
      )?.value;

      const zipCode = client?.codigoPostal.substring(client?.sufijoCodigoPostal.length || 0);

      if(isRepresentativeModal){        
        legalRepresentativeDataForm.setFields([
            { name: 'fullName', value: client?.nombre},
            { name: 'documentType', value: documentTypeValue},
            { name: 'documentNumber', value: client?.numeroDeDocumento},
            { name: 'cuit', value: client?.numeroDeCuit},
            { name: 'personType', value: typePersonValue},
            { name: 'province', value: provinceValue},
            { name: 'location', value: client?.codigoPostal },
            { name: 'codProvinciaInder', value: client},
            { name: 'codigoPostal', value: zipCode},
            { name: 'sufijoCodigoPostal', value: client?.sufijoCodigoPostal},
        ])
        setIsRepresentativeModal(false)
      }else{
        clientDataForm.setFields([
          { name: 'nroAsegurado', value: client?.codigo },
          { name: 'documentType', value: documentTypeValue },
          { name: 'documentNumber', value: client?.numeroDeDocumento },
          { name: 'fullName', value: client?.nombre },
          { name: 'cuit', value: client?.numeroDeCuit },
          { name: 'personType', value: typePersonValue },
          { name: 'province', value: provinceValue },
          { name: 'location', value: client?.codigoPostal },
          { name: 'codigoPostal', value: zipCode }, 
          { name: 'codProvinciaInder', value: client?.codProvinciaInder },
          { name: 'sufijoCodigoPostal', value: client?.sufijoCodigoPostal },
          { name: 'ivaCondition', value: ivaCondition },
          { name: 'address', value: client?.domicilio },
          { name: 'nationalityCode', value: client?.codigoNacionalidad },
          { name: 'sexCode', value: client?.codigoSexo },
          { name: 'perceptionAgent', value: client?.agentePercepcion },
          { name: 'companyTypeCode', value: client?.codigoTipoSociedad },
          { name: 'inscriptionNumber', value: client?.numeroInscripcion },
          { name: 'inscriptionDate', value: client?.fechaInscripcion },
          { name: 'contractDate', value: client?.fechaContrato },
        ]);
      }

      setClientListModalIsOpen(false);
    },
    [clients, ivaConditionList, documentTypeList, typePersonList, provinceList, isRepresentativeModal, clientDataForm]
  );

  const getClientList = useCallback(async () => {
    try {
      setIsLoadingClientList(true);
      const response = await QuotationService.getClientList();      
      if (response.data?.clientes?.length) {
        const tableDataGrid: Array<ClientDataTableGrid> =
          response.data.clientes.map(
            ({
              codigo,
              nombre,
              tipoDeDocumento,
              numeroDeCuit,
              numeroDeDocumento,
              descripcionIva,
              tipoPersona,
              codProvincia,
              codProvinciaInder,
              codigoPostal,
              sufijoCodigoPostal,
              codigoNacionalidad,
              descripcionNacionalidad,
              codigoSexo,
              descripcionSexo,
              codigoTipoSociedad,
              descripcionTipoSociedad,
              fechaInscripcion,
              numeroInscripcion,
              fechaContrato,
            }) => ({
              key: codigo,
              fullName: nombre,
              documentType: tipoDeDocumento,
              documentNumber: numeroDeDocumento,
              cuit: numeroDeCuit,
              ivaCondition: descripcionIva,
              tipoPersona,
              codProvincia,
              codProvinciaInder,
              codigoPostal,
              sufijoCodigoPostal,
              codigoNacionalidad,
              descripcionNacionalidad,
              codigoSexo,
              descripcionSexo,
              codigoTipoSociedad,
              descripcionTipoSociedad,
              fechaInscripcion,
              numeroInscripcion,
              fechaContrato,
            })
          );
        setClients(response.data.clientes);
        setTableClientList(tableDataGrid);
      }

      
    } catch (error) {console.error("error: " + error)} finally {
      setIsLoadingClientList(false);
    }
  }, [setIsLoadingClientList, setClients, setTableClientList]);

  const fillStepOneFromSavedQuotation = useCallback(async () => {
    const { cliente } = savedQuotation! as LoadSavedQuotations;

    await getLocalitiesPerProvince(cliente.provincia);

    clientDataForm.setFields([
      { name: 'nroAsegurado', value: cliente.nroAsegurado },
      { name: 'documentType', value: String(cliente.tipoDeDocumento) },
      { name: 'documentNumber', value: cliente.numeroDeDocumento },
      { name: 'fullName', value: cliente.nombreCompleto },
      { name: 'cuit', value: cliente.cuit ?? cliente.cuil },
      { name: 'personType', value: cliente.tipoDePersona },
      { name: 'province', value: cliente.provincia },
      { name: 'location', value: `${cliente.sufijoCodigoPostal}${cliente.codigoPostal}` },
      { name: 'codigoPostal', value: cliente.codigoPostal },
      { name: 'codProvinciaInder', value: cliente.codProvinciaInder },
      { name: 'sufijoCodigoPostal', value: cliente.sufijoCodigoPostal },
      { name: 'ivaCondition', value: String(cliente.codigoCondicionIva) },
    ]);
    
    setClientSelected({
      nroAsegurado: String(cliente.nroAsegurado),
      tipoDocumento: String(cliente.tipoDeDocumento),
      nroDocumento: String(cliente.numeroDeDocumento),
      nombre: fixEncoding(cliente.nombreCompleto),
      cuit: String((cliente.cuit ?? cliente.cuil) || 0),
      tipoPersona: cliente.tipoDePersona,
      codProvincia: cliente.provincia,
      codigoPostal: String(cliente.codigoPostal),
      codProvinciaInder: String(cliente.codProvinciaInder),
      sufijoCodigoPostal: String(cliente.sufijoCodigoPostal),
      condicionIVA: String(cliente.codigoCondicionIva)
    });

     setAdditionalDataClientSelected({
      domicilio: normalizeOptionalValue(cliente.domicilio),
      codigoNacionalidad: normalizeOptionalValue(cliente.codigoNacionalidad),
      codigoSexo: normalizeOptionalValue(cliente.codigoSexo),
      codigoTipoSociedad: normalizeOptionalValue(cliente.codigoTipoSociedad),
      fechaContrato: normalizeOptionalValue(cliente.fechaContrato),
      fechaInscripcion: normalizeOptionalValue(cliente.fechaInscripcion),
      numeroInscripcion: normalizeOptionalValue(cliente.numeroInscripcion),
      agentePercepcion: normalizeOptionalValue(cliente.agentePercepcion),
      telefonoParticular: normalizeOptionalValue(cliente.telefonoParticular),
      telefonoLaboral: normalizeOptionalValue(cliente.telefonoLaboral),
      telefonoCelular: normalizeOptionalValue(cliente.telefonoCelular),
      paginaWeb: normalizeOptionalValue(cliente.paginaWeb),
      fechaNacimiento: normalizeOptionalValue(cliente.fechaNacimiento),
      lugarNacimiento: normalizeOptionalValue(cliente.lugarNacimiento),
      idPaisNacimiento: normalizeOptionalValue(cliente.idPaisNacimiento),
      idEstadoCivil: normalizeOptionalValue(cliente.idEstadoCivil),
      idRamaEconomica: normalizeOptionalValue(cliente.idRamaEconomica),
      numeroRuta: normalizeOptionalValue(cliente.numeroRuta),
      idProfesion: normalizeOptionalValue(cliente.idProfesion),
      email: normalizeOptionalValue(cliente.email),
      agenteRetencion: normalizeOptionalValue(cliente.agenteRetencion)
    });

  }, [
    setClientSelected, 
    clientDataForm, 
    savedQuotation,
    getLocalitiesPerProvince,
  ]);

  const fillStepOneFromRenewalQuotation = useCallback(async () => {
    const { cliente } = quotationToRenewal! as LoadQuotationToRenewResponse;
    
    await getLocalitiesPerProvince(cliente.provincia);

    clientDataForm.setFields([
      { name: 'nroAsegurado', value: cliente.nroAsegurado },
      { name: 'documentType', value: String(cliente.tipoDeDocumento) },
      { name: 'documentNumber', value: cliente.numeroDeDocumento },
      { name: 'fullName', value: cliente.nombreCompleto },
      { name: 'cuit', value: cliente.cuit ?? cliente.cuil },
      { name: 'personType', value: cliente.tipoDePersona },
      { name: 'province', value: cliente.provincia },
      { name: 'location', value: `${cliente.sufijoCodigoPostal}${cliente.codigoPostal}` },
      { name: 'codigoPostal', value: cliente.codigoPostal },
      { name: 'codProvinciaInder', value: cliente.codProvinciaInder },
      { name: 'sufijoCodigoPostal', value: cliente.sufijoCodigoPostal },
      { name: 'ivaCondition', value: String(cliente.codigoCondicionIva) },
    ]);
    
    setClientSelected({
      nroAsegurado: String(cliente.nroAsegurado),
      tipoDocumento: String(cliente.tipoDeDocumento),
      nroDocumento: String(cliente.numeroDeDocumento),
      nombre: cliente.nombreCompleto,
      cuit: String((cliente.cuit ?? cliente.cuil) || 0),
      tipoPersona: cliente.tipoDePersona,
      codProvincia: cliente.provincia,
      codigoPostal: String(cliente.codigoPostal),
      codProvinciaInder: String(cliente.codProvinciaInder),
      sufijoCodigoPostal: String(cliente.sufijoCodigoPostal),
      condicionIVA: String(cliente.codigoCondicionIva)
    });

    setAdditionalDataClientSelected({
      domicilio: normalizeOptionalValue(cliente.domicilio),
      codigoNacionalidad: normalizeOptionalValue(cliente.codigoNacionalidad),
      codigoSexo: normalizeOptionalValue(cliente.codigoSexo),
      codigoTipoSociedad: normalizeOptionalValue(cliente.codigoTipoSociedad),
      fechaContrato: normalizeOptionalValue(cliente.fechaContrato),
      fechaInscripcion: normalizeOptionalValue(cliente.fechaInscripcion),
      numeroInscripcion: normalizeOptionalValue(cliente.numeroInscripcion),
      agentePercepcion: normalizeOptionalValue(cliente.agentePercepcion),
      telefonoParticular: normalizeOptionalValue(cliente.telefonoParticular),
      telefonoLaboral: normalizeOptionalValue(cliente.telefonoLaboral),
      telefonoCelular: normalizeOptionalValue(cliente.telefonoCelular),
      paginaWeb: normalizeOptionalValue(cliente.paginaWeb),
      fechaNacimiento: normalizeOptionalValue(cliente.fechaNacimiento),
      lugarNacimiento: normalizeOptionalValue(cliente.lugarNacimiento),
      idPaisNacimiento: normalizeOptionalValue(cliente.idPaisNacimiento),
      idEstadoCivil: normalizeOptionalValue(cliente.idEstadoCivil),
      idRamaEconomica: normalizeOptionalValue(cliente.idRamaEconomica),
      numeroRuta: normalizeOptionalValue(cliente.numeroRuta),
      idProfesion: normalizeOptionalValue(cliente.idProfesion),
      email: normalizeOptionalValue(cliente.email),
      agenteRetencion: normalizeOptionalValue(cliente.agenteRetencion)
    });

  }, [
    setClientSelected, 
    clientDataForm, 
    quotationToRenewal,
    getLocalitiesPerProvince,
  ]);


  const fillStepOneServices = useCallback(async () => {    
    getClientList();
    getGenericDataForm();
  }, [
    getClientList,
    getGenericDataForm,
  ]);

  useEffect(() => {
    if(quotationInfo && currentStep === QuotationStep.ONE && !loadingSavedQuotation) fillStepOneServices();
  }, [currentStep, loadingSavedQuotation, quotationInfo]);

  useEffect(() => {
    if(savedQuotation !== undefined && loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.INIT_LOADING) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_SERVICES_LOADING);
      fillStepOneServices();
    }
  }, [savedQuotation, loadSaveQuotationState]);

  useEffect(() => {
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.INIT_LOADING_RENEWAL) {      
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_SERVICES_LOADING_RENEWAL);
      fillStepOneServices();
    }
  }, [quotationToRenewal, renewalQuotationState]);

  useEffect(() => {
    if(
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_SERVICES_LOADING &&
      ivaConditionList &&
      documentTypeList &&
      typePersonList &&
      provinceList &&
      clients &&
      tableClientList
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_SERVICES_LOADED);
    }
  }, [
    loadSaveQuotationState,
    ivaConditionList,
    documentTypeList,
    typePersonList,
    provinceList,
    clients,
    tableClientList
  ]);

  useEffect(() => {
    if(
      renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_SERVICES_LOADING_RENEWAL &&
      ivaConditionList &&
      documentTypeList &&
      typePersonList &&
      provinceList &&
      clients &&
      tableClientList
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_SERVICES_LOADED_RENEWAL);
    }
  }, [
    renewalQuotationState,
    ivaConditionList,
    documentTypeList,
    typePersonList,
    provinceList,
    clients,
    tableClientList
  ]);

  useEffect(() => {
    if(loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_SERVICES_LOADED) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLING);
      fillStepOneFromSavedQuotation();
    }
  }, [loadSaveQuotationState, setLoadSaveQuotationState]);

  useEffect(() => {    
    if(renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_SERVICES_LOADED_RENEWAL) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLING_RENEWAL);
      fillStepOneFromRenewalQuotation();
    }
  }, [renewalQuotationState, setRenewalQuotationState]);

  useEffect(() => {
    if(
      loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLING &&
      clientSelected
    ) {
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED);
    }
  }, [loadSaveQuotationState, clientSelected, setLoadSaveQuotationState]);

  useEffect(() => {
    if(
      renewalQuotationState === LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLING_RENEWAL &&
      clientSelected
    ) {
      setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL);
    }
  }, [renewalQuotationState, clientSelected, setRenewalQuotationState]);

  const values = useMemo(
    () => ({
      clients,
      setClients,
      tableClientList,
      setTableClientList,
      clientSelected,
      setClientSelected,
      legalRepresentativeSelected,
      setLegalRepresentativeSelected,
      stepOneForm,
      setStepOneForm,
      clientDataForm,
      legalRepresentativeDataForm,
      onSelectClientOnTable,
      clientListModalIsOpen,
      setClientListModalIsOpen,
      isLoadingClientList,
      isRepresentativeModal,
      additionalDataClientSelected,
      setIsRepresentativeModal,
      setAdditionalDataClientSelected
    }),
    [
      clients,
      setClients,
      tableClientList,
      setTableClientList,
      clientSelected,
      setClientSelected,
      legalRepresentativeSelected,
      setLegalRepresentativeSelected,
      stepOneForm,
      setStepOneForm,
      clientDataForm,
      legalRepresentativeDataForm,
      onSelectClientOnTable,
      clientListModalIsOpen,
      setClientListModalIsOpen,
      isLoadingClientList,
      isRepresentativeModal,
      setIsRepresentativeModal,
      setAdditionalDataClientSelected
    ]
  );

  return (
    <StepOneFormContext.Provider value={values}>
      {children}
    </StepOneFormContext.Provider>
  );
};
